import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Briefcase, AlertTriangle, CheckCircle2, ArrowUpRight, Save, ShieldCheck, FileCheck, Camera, FileBadge, ListChecks, MessagesSquare } from "lucide-react";

const STAGE_STYLES = {
  "Not Started": "bg-slate-100 text-slate-500 border-slate-200",
  "In Progress": "bg-blue-50 text-blue-800 border-blue-200",
  "Needs Attention": "bg-amber-50 text-amber-800 border-amber-200",
  "Complete": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Pending": "bg-slate-50 text-slate-500 border-slate-200",
};
const STAGE_ORDER = ["Scope & Environment", "Implementation Review", "Reconciliation", "Documentation", "Evidence", "SSP", "Assessment Readiness"];
const ENGAGEMENT_STATUSES = ["Active", "On Hold", "Assessment Prep", "Assessment Scheduled", "Closed"];

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-navy font-semibold text-sm mb-3">
        {Icon && <Icon className="h-4 w-4 text-gold" />} {title}
      </div>
      {children}
    </div>
  );
}
function Stat({ label, value, tone }) {
  const tones = { good: "text-emerald-700", warn: "text-amber-700", info: "text-blue-700", muted: "text-slate-500" };
  return (
    <div>
      <div className={`text-2xl font-display font-bold ${tones[tone] || "text-navy"}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

export default function EngagementWorkspace() {
  const { customerId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [meta, setMeta] = useState({ complyx_lead: "", target_assessment_date: "", engagement_status: "Active", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/engagements/${customerId}`)
      .then((r) => {
        setD(r.data);
        const m = r.data.meta || {};
        setMeta({
          complyx_lead: m.complyx_lead || "",
          target_assessment_date: m.target_assessment_date || "",
          engagement_status: m.engagement_status || "Active",
          notes: m.notes || "",
        });
      })
      .catch(() => nav("/operations/engagements"));
  }, [customerId, nav]);
  useEffect(() => { load(); }, [load]);

  const saveMeta = () => {
    setSaving(true);
    api.put(`/engagements/${customerId}/meta`, meta)
      .then(() => toast.success("Engagement details saved."))
      .catch((e) => toast.error(e.response?.data?.detail || "Save failed"))
      .finally(() => setSaving(false));
  };

  if (!d) return <div className="p-8 text-muted-foreground text-sm">Loading engagement…</div>;

  return (
    <div data-testid="engagement-workspace-page">
      <header className="bg-navy px-6 md:px-8 py-6">
        <button onClick={() => nav("/operations/engagements")} className="text-slate-300 hover:text-white text-xs flex items-center gap-1 mb-2" data-testid="engagement-back-btn">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Customer Engagements
        </button>
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <Briefcase className="h-6 w-6 text-gold" /> {d.customer_name || "Customer"}
        </h1>
        <p className="text-slate-300 text-sm mt-2">
          {d.cmmc_level} · Current stage: <span className="text-gold font-medium">{d.current_stage}</span> · {d.readiness}
          {d.implementation_partner ? ` · Partner: ${d.implementation_partner}` : ""}
        </p>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        {/* Workflow progress */}
        <Card title="Workflow Progress" icon={CheckCircle2} testid="engagement-stages">
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
            {STAGE_ORDER.map((s) => (
              <div key={s} className={`rounded border px-2.5 py-2 text-center ${STAGE_STYLES[d.stages?.[s]] || "bg-slate-50 text-slate-500 border-slate-200"}`} data-testid={`stage-${s.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <div className="text-[11px] font-medium leading-tight">{s}</div>
                <div className="text-[10px] mt-1 opacity-80">{d.stages?.[s] || "—"}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Controls" icon={ShieldCheck} testid="card-controls">
            <div className="grid grid-cols-4 gap-2">
              <Stat label="Implemented" value={d.controls.implemented} tone="good" />
              <Stat label="Planned" value={d.controls.planned} tone="info" />
              <Stat label="N/A" value={d.controls.na} tone="muted" />
              <Stat label="Manual" value={d.controls.manual} tone="warn" />
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">{d.controls.total} controls in latest reconciliation ({d.reconciliation_status || "—"})</div>
          </Card>
          <Card title="Evidence" icon={Camera} testid="card-evidence">
            <div className="grid grid-cols-4 gap-2">
              <Stat label="Accepted" value={d.evidence.accepted} tone="good" />
              <Stat label="Open" value={d.evidence.open} tone="warn" />
              <Stat label="Conflicts" value={d.evidence.conflicts} tone="warn" />
              <Stat label="Failed" value={d.evidence.failed} tone="warn" />
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">{d.evidence.env_total} environment evidence items</div>
          </Card>
          <Card title="Documentation" icon={FileCheck} testid="card-documentation">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Approved" value={d.documentation.approved} tone="good" />
              <Stat label="Draft" value={d.documentation.draft} tone="info" />
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">Policies & procedures</div>
          </Card>
          <Card title="System Security Plan" icon={FileBadge} testid="card-ssp">
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Status" value={d.ssp.status || "None"} tone={d.ssp.status === "Approved" ? "good" : "info"} />
              <Stat label="Version" value={d.ssp.version || "—"} tone="muted" />
              <Stat label="Manual" value={d.ssp.needs_manual_review} tone="warn" />
            </div>
            {d.ssp.needs_update && <div className="text-[11px] text-amber-700 mt-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Approved SSP requires an update</div>}
          </Card>
          <Card title="Tasks" icon={ListChecks} testid="card-tasks">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Open" value={d.tasks.open} tone="info" />
              <Stat label="High Priority" value={d.tasks.high} tone="warn" />
            </div>
          </Card>
          <Card title="Questions" icon={MessagesSquare} testid="card-questions">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Open" value={d.questions.open} tone="info" />
              <Stat label="Tech Profile" value={d.tech_profile} tone="muted" />
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Work by owner */}
          <Card title="Work by Owner" icon={ListChecks} testid="card-owners">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Client</span><span className="font-semibold text-navy">{d.owners.client}</span></div>
              <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Implementation Partner</span><span className="font-semibold text-navy">{d.owners.implementer}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">ComplyX</span><span className="font-semibold text-navy">{d.owners.complyx}</span></div>
            </div>
          </Card>

          {/* Blockers */}
          <Card title="Blockers" icon={AlertTriangle} testid="card-blockers">
            {(!d.blockers || d.blockers.length === 0) ? (
              <div className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> No open blockers.</div>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {d.blockers.map((b, i) => (
                  <li key={i} className="flex justify-between items-center" data-testid="blocker-item">
                    <span className="text-amber-800">{b.type}</span>
                    <span className="text-xs font-semibold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">{b.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Engagement details editor */}
          <Card title="Engagement Details" icon={Briefcase} testid="card-meta">
            <div className="space-y-3">
              <div>
                <Label className="text-xs">ComplyX Lead</Label>
                <Input value={meta.complyx_lead} onChange={(e) => setMeta({ ...meta, complyx_lead: e.target.value })} placeholder="e.g. Steve" data-testid="meta-lead" />
              </div>
              <div>
                <Label className="text-xs">Target Assessment Date</Label>
                <Input type="date" value={meta.target_assessment_date} onChange={(e) => setMeta({ ...meta, target_assessment_date: e.target.value })} data-testid="meta-date" />
              </div>
              <div>
                <Label className="text-xs">Engagement Status</Label>
                <Select value={meta.engagement_status} onValueChange={(v) => setMeta({ ...meta, engagement_status: v })}>
                  <SelectTrigger data-testid="meta-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENGAGEMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Internal Notes</Label>
                <Textarea value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} rows={3} placeholder="Internal engagement notes…" data-testid="meta-notes" />
              </div>
              <Button onClick={saveMeta} disabled={saving} className="bg-gold text-navy hover:bg-gold-dark w-full" data-testid="meta-save-btn">
                <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Details"}
              </Button>
            </div>
          </Card>
        </div>

        {/* Next actions */}
        <Card title="Recommended Next Actions" icon={ArrowUpRight} testid="card-next-actions">
          {(!d.next_actions || d.next_actions.length === 0) ? (
            <div className="text-sm text-muted-foreground">No outstanding next actions.</div>
          ) : (
            <ul className="divide-y divide-border">
              {d.next_actions.map((a, i) => (
                <li key={i} className="py-2.5 flex items-center justify-between gap-3" data-testid="next-action-item">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium rounded px-1.5 py-0.5 bg-slate-100 text-slate-600">{a.owner}</span>
                    <span className="text-sm text-navy">{a.text}</span>
                  </div>
                  <Link to={a.link} className="text-xs text-gold-dark hover:underline flex items-center gap-1 shrink-0" data-testid="next-action-link">
                    Open <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
